/**
 * gen_625.js — Nature d'une fraction (entier / décimal non entier / non décimal)
 * Cluster 13, Attendu 625 — 8 générateurs
 */

import { shuffle, randomChoice, makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeClasser } from '../../../utils';
import { mkFrac, toDecimalString } from './fraction.js';

// ─── Helpers ──────────────────────────────────────────────────

const OPT = { niveau: '6eme', matiere: 'maths', attendu: 625 };

function fracStr(a, b) { return `$\\dfrac{${a}}{${b}}$`; }

const exprElement = (katexStr) => ({
    version: 1,
    elements: [{ type: 'expression', config: { contenu: katexStr } }],
});

/**
 * Convertit a/b en string décimal virgule française.
 * Retourne null si non décimal.
 */
function toDecFr(a, b) {
  const f = mkFrac(a, b);
  return toDecimalString(f);
}

// Pools pré-constitués
const POOL_ENTIER = [
  [20, 4], [15, 3], [36, 6], [77, 11], [100, 25], [48, 8],
  [24, 3], [45, 9], [56, 7], [72, 8], [90, 10], [60, 12],
  [42, 6], [55, 5], [63, 7], [84, 12], [96, 8], [110, 11]
];
const POOL_DECIMAL = [
  [7, 4], [3, 8], [9, 5], [11, 2], [13, 4], [17, 8], [21, 20],
  [1, 2], [3, 4], [1, 4], [1, 5], [2, 5], [3, 5], [4, 5],
  [1, 8], [5, 8], [7, 8], [7, 2], [9, 4], [11, 4],
  [9, 2], [19, 4], [23, 8], [27, 4], [31, 8], [37, 20]
];
const POOL_NON_DEC = [
  [1, 3], [10, 3], [8, 7], [4, 9], [5, 6], [7, 11], [2, 7], [14, 9],
  [1, 6], [5, 3], [7, 3], [11, 6], [13, 7], [2, 3], [5, 7],
  [10, 7], [11, 9], [1, 7], [4, 3], [8, 3], [17, 6], [19, 3],
  [25, 7], [10, 6], [7, 6], [11, 3], [13, 9], [16, 7]
];

// ─── T1: Classer ──────────────────────────────────────────────

function nature_classer() {
  const ents = shuffle(POOL_ENTIER).slice(0, 2);
  const decs = shuffle(POOL_DECIMAL).slice(0, 2);
  const nds = shuffle(POOL_NON_DEC).slice(0, 2);

  const categories = [
    { name: 'Nombre entier', items: ents.map(([a, b]) => fracStr(a, b)) },
    { name: 'Décimal non entier', items: decs.map(([a, b]) => fracStr(a, b)) },
    { name: 'Non décimal', items: nds.map(([a, b]) => fracStr(a, b)) }
  ];

  const texte = '<p>Classe chaque fraction dans la bonne catégorie.</p>';

  return {
    ...makeClasser(categories, texte, { ...OPT, label: 'Classer des fractions selon leur nature (entier, décimal, non décimal)' }),
    _meta: { generatorKey: 'nature_classer' }
  };
}

// ─── T2: QCM nature ──────────────────────────────────────────

function nature_qcm() {
  const cat = randomChoice(['entier', 'decimal', 'non_decimal']);
  const pool = cat === 'entier' ? POOL_ENTIER : cat === 'decimal' ? POOL_DECIMAL : POOL_NON_DEC;
  const [a, b] = randomChoice(pool);

  const labels = { entier: 'Nombre entier', decimal: 'Nombre décimal non entier', non_decimal: 'Nombre non décimal' };
  const correct = labels[cat];
  const choix = ['Nombre entier', 'Nombre décimal non entier', 'Nombre non décimal'];

  const texte = `<p>Quelle est la nature du nombre représenté par la fraction {{element:0}} ?</p>`;

  return {
    ...makeQcm(choix, correct, texte, { ...OPT, label: 'Identifier la nature d\'une fraction' }),
    // Fraction BRUTE du pool (revue 2026-08-02) : renderFrac(mkFrac(20, 4))
    // affichait « 5 » — plus de fraction du tout, et la réponse offerte
    // (33 % des tirages, toute la catégorie entier).
    elements_additionnels: exprElement(`\\dfrac{${a}}{${b}}`),
    _meta: { generatorKey: 'nature_qcm' }
  };
}

// ─── T3: V/F affirmations ─────────────────────────────────────

function nature_vf() {
  const lines = [];
  const answers = [];
  // Dédup GLOBALE des fractions (revue 2026-08-02) : la même fraction
  // pouvait apparaître dans deux lignes contradictoires (« est décimal »
  // Faux + « n'est pas décimal » Vrai) — réponse déductible, 2 % des
  // tableaux mesurés. Chaque fraction ne sert qu'une fois.
  const usedF = new Set();
  const tirer = (pool) => {
    let a, b;
    do { [a, b] = randomChoice(pool); } while (usedF.has(`${a}/${b}`));
    usedF.add(`${a}/${b}`);
    return [a, b];
  };

  // Génère 4 affirmations (2V + 2F ou 3V + 1F)
  const nbVrai = randomChoice([2, 3]);

  // Affirmation vraie : entier
  const [ae, be] = tirer(POOL_ENTIER);
  lines.push(`${fracStr(ae, be)} est un nombre entier`);
  answers.push(0); // Vrai

  // Affirmation vraie : décimal
  const [ad, bd] = tirer(POOL_DECIMAL);
  lines.push(`${fracStr(ad, bd)} est un nombre décimal`);
  answers.push(0); // Vrai

  if (nbVrai === 3) {
    // 3ème vraie : non décimal
    const [an, bn] = tirer(POOL_NON_DEC);
    lines.push(`${fracStr(an, bn)} n'est pas un nombre décimal`);
    answers.push(0); // Vrai
  }

  // Affirmations fausses
  // Fausse 1 : non-décimal présenté comme décimal
  const [an1, bn1] = tirer(POOL_NON_DEC);
  lines.push(`${fracStr(an1, bn1)} est un nombre décimal`);
  answers.push(1); // Faux

  if (nbVrai === 2) {
    // Fausse 2 : décimal présenté comme non-décimal
    const [ad2, bd2] = tirer(POOL_DECIMAL);
    lines.push(`${fracStr(ad2, bd2)} n'est pas un nombre décimal`);
    answers.push(1); // Faux
  }

  // Mélanger
  const idx = shuffle([...Array(lines.length).keys()]);
  const orderedLines = idx.map(i => lines[i]);
  const orderedAnswers = idx.map(i => answers[i]);

  const texte = '<p>Dis si chaque affirmation est vraie ou fausse.</p>';

  return {
    ...makeVraiFaux(orderedLines, orderedAnswers, texte, { ...OPT, label: 'Vrai/Faux sur la nature de fractions' }),
    _meta: { generatorKey: 'nature_vf' }
  };
}

/* ──────────────────────────────────────────────────────────────
 * SUPPRIMÉ 2026-08-02 (nature_relier) : « relie chaque décimal à sa
 * fraction » était une pure conversion sans dimension « nature » —
 * trait pour trait la carte « Valeur décimale d'une fraction » de 620
 * (valeur_decimale_relier), déjà revue et calibrée. Frontière :
 * 620 calcule des valeurs, 625 juge des natures.
 * ────────────────────────────────────────────────────────────── */

// ─── T5: Saisie valeur décimale exacte ────────────────────────

function nature_saisie() {
  const isDecCase = randomChoice([true, false]);

  if (isDecCase) {
    const [a, b] = randomChoice(POOL_DECIMAL.filter(([a, b]) => a % b !== 0));
    const dec = toDecFr(a, b);
    const texte = `<p>Écris la valeur décimale exacte de {{element:0}}.</p>`;
    return {
      ...makeSaisie(dec, texte, { ...OPT, strict: false, label: 'Écrire la valeur décimale exacte d\'une fraction' }),
      elements_additionnels: exprElement(`\\dfrac{${a}}{${b}}`),
      _meta: { generatorKey: 'nature_saisie' }
    };
  } else {
    const [a, b] = randomChoice(POOL_NON_DEC);
    const texte = `<p>Écris la valeur décimale exacte de {{element:0}}. Si la division ne s'arrête pas, écris « impossible ».</p>`;
    return {
      ...makeSaisie('impossible', texte, { ...OPT, strict: false, label: 'Reconnaître qu\'une fraction n\'est pas décimale' }),
      elements_additionnels: exprElement(`\\dfrac{${a}}{${b}}`),
      _meta: { generatorKey: 'nature_saisie' }
    };
  }
}

// ─── T6: Texte à trous drag (méthode) ────────────────────────

function nature_methode_drag() {
  // Utilise les pools communs (cohérence avec les autres formats de gen_625).
  // 50/50 entre décimal et non-décimal pour équilibrer les cas pédagogiques.
  const isDecimal = Math.random() < 0.5;
  const pool = isDecimal ? POOL_DECIMAL : POOL_NON_DEC;
  const [a, b] = randomChoice(pool);

  // « cette fraction » : la fraction est déjà affichée en vraie écriture
  // dans la consigne (élément expression) — « 10/3 » à plat banni.
  const phrase = `Pour savoir si cette fraction est un nombre décimal, on effectue la {{division}} de {{${a}}} par {{${b}}}. Si la division {{s'arrête}}, alors c'est un nombre {{décimal}}. Si elle ne s'arrête pas, c'est un nombre {{non décimal}}.`;

  const distPool = ['multiplication', 'continue', 'entier', 'négatif', 'fraction', 'soustraction'];
  const distracteurs = shuffle(distPool).slice(0, 3);

  const texte = `<p>Complète la phrase pour déterminer la nature de {{element:0}}.</p>`;

  return {
    ...makeTexteTrou(phrase, texte, { ...OPT, mode: 'drag', distracteurs, label: 'Méthode pour déterminer la nature d\'une fraction (drag)' }),
    elements_additionnels: exprElement(`\\dfrac{${a}}{${b}}`),
    _meta: { generatorKey: 'nature_methode_drag' }
  };
}

// ─── T7: QCM exact ou approché ────────────────────────────────

function nature_exact_approche() {
  const isExact = randomChoice([true, false]);

  // 3 choix — "Indéterminable" est un distracteur pédagogique : un élève
  // qui doute peut être tenté mais n'est jamais la bonne réponse dans ce contexte.
  const CHOIX = ['Exacte', 'Approchée', 'Indéterminable'];
  if (isExact) {
    const [a, b] = randomChoice(POOL_DECIMAL.filter(([a, b]) => a % b !== 0));
    const dec = toDecFr(a, b);
    const texte = `<p>Un élève a écrit : {{element:0}} = ${dec}. Cette écriture est-elle exacte ou approchée ?</p>`;
    return {
      ...makeQcm(CHOIX, 'Exacte', texte, { ...OPT, label: 'Distinguer écriture décimale exacte et approchée' }),
      elements_additionnels: exprElement(`\\dfrac{${a}}{${b}}`),
      _meta: { generatorKey: 'nature_exact_approche' }
    };
  } else {
    const [a, b] = randomChoice(POOL_NON_DEC);
    const approx = (a / b).toFixed(2).replace('.', ',');
    const texte = `<p>Un élève a écrit : {{element:0}} = ${approx}. Cette écriture est-elle exacte ou approchée ?</p>`;
    return {
      ...makeQcm(CHOIX, 'Approchée', texte, { ...OPT, label: 'Reconnaître une valeur approchée' }),
      elements_additionnels: exprElement(`\\dfrac{${a}}{${b}}`),
      _meta: { generatorKey: 'nature_exact_approche' }
    };
  }
}

// ─── T8: QCM multiple — sélectionner les décimaux ─────────────

function nature_select_decimaux() {
  // Construire un mélange : 3 décimaux + 3 non-décimaux (ou 4+2)
  const nbDec = randomChoice([3, 4]);
  const nbNonDec = 6 - nbDec;

  // Pool : inclure entiers comme décimaux (entier ⊂ décimal)
  const decPool = shuffle([...POOL_DECIMAL, ...POOL_ENTIER]);
  const ndPool = shuffle(POOL_NON_DEC);

  const chosen = [];
  const usedStr = new Set();

  // Pick décimaux
  for (const [a, b] of decPool) {
    if (chosen.length >= nbDec) break;
    const s = `${a}/${b}`;
    if (!usedStr.has(s)) {
      usedStr.add(s);
      chosen.push({ a, b, isDec: true });
    }
  }

  // Pick non-décimaux
  for (const [a, b] of ndPool) {
    if (chosen.filter(c => !c.isDec).length >= nbNonDec) break;
    const s = `${a}/${b}`;
    if (!usedStr.has(s)) {
      usedStr.add(s);
      chosen.push({ a, b, isDec: false });
    }
  }

  const shuffled = shuffle(chosen);
  const choix = shuffled.map(c => fracStr(c.a, c.b));
  const correctLabels = [];
  const LETTRES = 'ABCDEFGH';
  shuffled.forEach((c, i) => {
    if (c.isDec) correctLabels.push(LETTRES[i]);
  });

  const texte = '<p>Parmi les fractions suivantes, sélectionne celles qui sont des nombres décimaux (y compris les entiers).</p>';

  const payload = {
    type: 'qcm',
    texte,
    contenu: {
      choix,
      bonne_reponse: correctLabels.sort(),
      multi: true
    },
    elements_additionnels: null,
    niveau: '6eme', matiere: 'maths', attendu: 625,
    _meta: { generatorKey: 'nature_select_decimaux' }
  };

  return payload;
}

// ─── Export ───────────────────────────────────────────────────

const GENERATORS = {
  nature_classer,
  nature_qcm,
  nature_vf,
  nature_saisie,
  nature_methode_drag,
  nature_exact_approche,
  nature_select_decimaux
};

const FORMAT_LABELS = {
    classer: 'Classer', qcm: 'QCM', vf: 'Vrai/Faux',
    saisie: 'Saisie', methode_drag: 'Drag',
    exact_approche: 'Exact ou approché', select_decimaux: 'Sélection multiple',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'nature', label: 'Nature d\'une fraction (entier, décimal, non décimal)', formatsList: buildFormats('nature') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Pas de calculatrice : la nature se juge en posant/pensant la division
    // (même arbitrage que 620, revue 2026-08-02)
    payload.contenu = { ...payload.contenu, calculatrice: false };
    payload._meta.generatorKey = key;
    return payload;
};
